import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { of, from, Observable } from 'rxjs';
import { catchError, exhaustMap, map, tap, switchMap, finalize } from 'rxjs/operators';
import { LocalStorageService } from '../../services/local-storage.service';
import { AuthService } from '../../services/auth.service';
import { MenuService } from '../../services/menu.service';
import { setSidebarItems } from '../ui/ui.actions';
import { Router } from '@angular/router';
import {
  tryAutoLogin,
  autoLoginSuccess,
  autoLoginFailure,
  login,
  loginSuccess,
  loginFailure,
  logout,
  logoutSuccess,
  logoutFailure,
  requestPasswordReset,
  passwordResetSuccess,
  passwordResetFailure,
  recoverPassword,
  recoverPasswordSuccess,
  recoverPasswordFailure,
} from './auth.actions';
import { OneSignalLoaderService } from '../../services/onesignal-loader.service';
import { ScreenSpinnerService } from '../../services/screen-spinner.service';
import { LoginRequestMapper } from '../../mappers/login-request.mapper';
import { AUTH_USER_PROPERTY } from './auth.config';

@Injectable()
export class AuthEffects {
  tryAutoLogin$: Observable<Action>;
  login$: Observable<Action>;
  logout$: Observable<Action>;
  loginSuccess$: Observable<Action>;
  setMenuOnLogin$: Observable<Action>;
  requestPasswordReset$: Observable<Action>;
  recoverPassword$: Observable<Action>;
  recoverPasswordSuccess$: Observable<Action>;
  // Opcional: logoutSuccess$: any;

  constructor(
    private _actions$: Actions,
    private _authService: AuthService,
    private _menuService: MenuService,
    private _router: Router,
    private _localStorageService: LocalStorageService,
    private _oneSignalService: OneSignalLoaderService,
    private _spiService: ScreenSpinnerService,
    @Inject(AUTH_USER_PROPERTY) private _userProperty: string
  ) {
    this.tryAutoLogin$ = createEffect(() =>
      this._actions$.pipe(
        ofType(tryAutoLogin),
        exhaustMap(() => {
          const token = this._localStorageService.getItem('authToken');
          if (!token) {
            return of(autoLoginFailure());
          }
          return this._authService.getProfileFromToken().pipe(
            map((response) => {
              return autoLoginSuccess({ user: response, token: token });
            }),
            catchError((_err) => {
              this._localStorageService.removeItem('authToken'); // Clean up invalid token
              return of(autoLoginFailure());
            })
          );
        })
      )
    );

    this.login$ = createEffect(() =>
      this._actions$.pipe(
        ofType(login),
        exhaustMap((action) => {
          // Función para hacer login, extraída para evitar duplicación de código
          const doLogin = () => {
            const subId = this._oneSignalService.pushSubscriptionId;
            return this._authService.login(LoginRequestMapper.toUserAdminLoginDto(action)).pipe(
              map((response) => {
                return loginSuccess({
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  user: (response.session as any)[this._userProperty],
                  token: response.session.token,
                });
              }),
              catchError((error) => of(loginFailure({ error: error.toString() })))
            );
          };

          return doLogin();

          /*
          // Si ya tiene permisos, hace login directamente
          if (this._oneSignalService.hasPermission) {
            return doLogin();
          }

          // Si no tiene permisos, los solicita primero y luego hace login
          return from(
            this._oneSignalService
              .requestPushPermission()
              .then((_granted) => {})
              .catch((_error) => {})
              // Siempre continúa con el login, incluso si falla la solicitud de permisos
              .then(() => {})
          ).pipe(
            // Después de solicitar permisos (exitosamente o no), hacer login
            switchMap(() => doLogin())
          );*/
        })
      )
    );

    this.loginSuccess$ = createEffect(
      () =>
        this._actions$.pipe(
          ofType(loginSuccess),
          tap((action) => {
            this._localStorageService.setItem('authToken', action.token);
            if (this._userProperty === 'userAdmin') {
              this._router.navigate(['/account/residential-developments']);
            } else {
              this._router.navigate(['/account/dashboard']);
            }
          })
        ),
      { dispatch: false } // No despacha una nueva acción
    );

    this.logout$ = createEffect(() =>
      this._actions$.pipe(
        ofType(logout),
        tap(() => this._spiService.show('app')),
        exhaustMap(() =>
          // Primero llamar al servicio de logout
          this._authService.logout().pipe(
            map(() => {
              // Eliminar token solo después de una respuesta exitosa
              this._localStorageService.removeItem('authToken');
              // Redirigir al login después del logout exitoso
              this._router.navigate(['/login']);
              return logoutSuccess();
            }),
            catchError((error) => of(logoutFailure({ error: error.toString() }))),
            finalize(() => this._spiService.hide('app'))
          )
        )
      )
    );

    this.setMenuOnLogin$ = createEffect(() =>
      this._actions$.pipe(
        ofType(loginSuccess, autoLoginSuccess),
        switchMap(() => {
          // const user = select(selectUser);
          return this._menuService.getFilteredItemsForCurrentUser();
        }),
        map((items) => setSidebarItems({ items }))
      )
    );

    this.requestPasswordReset$ = createEffect(() =>
      this._actions$.pipe(
        ofType(requestPasswordReset),
        exhaustMap((action) => {
          const resetPassword = {
            email: action.email,
            token: '',
            newPassword: '',
          };

          return this._authService.requestResetPasswordCode(resetPassword).pipe(
            map(() => passwordResetSuccess()),
            catchError((error) => of(passwordResetFailure({ error: error.toString() })))
          );
        })
      )
    );

    this.recoverPassword$ = createEffect(() =>
      this._actions$.pipe(
        ofType(recoverPassword),
        exhaustMap((action) => {
          const changePassword = {
            token: action.token,
            password: action.newPassword,
            email: '', // El email podría no ser necesario si el token ya lo identifica
          };

          return this._authService.resetPassword(changePassword).pipe(
            map((response) => {
              return recoverPasswordSuccess({
                token: response.session.token,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                user: (response.session as any)[this._userProperty],
              });
            }),
            catchError((error) => of(recoverPasswordFailure({ error: error.toString() })))
          );
        })
      )
    );

    this.recoverPasswordSuccess$ = createEffect(
      () =>
        this._actions$.pipe(
          ofType(recoverPasswordSuccess),
          tap((_action) => {
            // No auto login
          })
        ),
      { dispatch: false }
    );
  }
}
